/**
 * 共有用の縦長カード（1080x1920）を Canvas で描く。
 *
 * 以前のカードは「数字とハッシュタグ」だけで、このゲームのいちばん強い素材である
 * 料理と景色が写っていなかった。見た人が「自分もやりたい」と思う材料を載せる。
 *
 * - 背景：いま映っていた県の景色
 * - 真ん中：到達した料理の絵を大きく
 * - 進化の列：届いた段は絵、まだの段は「？」（続きが気になる）
 * - 「あと○段で最上位」
 * - 結果画面で選んだ名場面（最高Lv・最大コンボ・最後の盤面）の盤面と、最大コンボ（2026-10-09）
 *   盤面があるときは、左に盤面・右に到達した料理の2列にする
 */

import type { ShareableResult } from './ShareSystem';

export interface ShareVisual {
  regionName: string;
  /** レベル順の料理。 */
  items: ReadonlyArray<{ level: number; name: string; iconUrl: string }>;
  reachedLevel: number;
  /** 背景に使う景色の画像（なければグラデーション）。 */
  sceneUrl?: string;
  host: string;
  hashtags: string;
  /** 今日の県で遊んだとき（例: 今日の県 9/24）。 */
  dailyLabel?: string;
  /** 結果画面で選んだ名場面の盤面（ゲームオーバーのときだけ。選び直すと差し替わる）。 */
  moment?: { image: HTMLCanvasElement; caption: string; kind: 'level' | 'combo' | 'final' } | null;
  /** このプレイの最大コンボ。 */
  maxCombo?: number;
}

const W = 1080;
const H = 1920;
const FONT = '"Hiragino Sans", "Noto Sans JP", "Noto Sans CJK JP", "Yu Gothic", sans-serif';

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function drawCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement): void {
  const scale = Math.max(W / img.width, H / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
}

function drawContain(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  cx: number,
  cy: number,
  size: number,
): void {
  const scale = Math.min(size / img.width, size / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
}

function text(
  ctx: CanvasRenderingContext2D,
  value: string,
  x: number,
  y: number,
  size: number,
  color: string,
  weight = 700,
  maxWidth = W - 120,
): void {
  ctx.font = `${weight} ${size}px ${FONT}`;
  // 幅に収まらないときは字を小さくする（fillText の maxWidth だけだと横に潰れて読みにくい）
  let px = size;
  while (px > 20 && ctx.measureText(value).width > maxWidth) {
    px -= 2;
    ctx.font = `${weight} ${px}px ${FONT}`;
  }
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(value, x, y, maxWidth);
}

function roundedPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  // roundRect は古い Safari に無い
  if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, w, h, r);
  else ctx.rect(x, y, w, h);
}

/** 盤面の写真を枠に収めて置く（縦横比はそのまま）。 */
function drawBoard(
  ctx: CanvasRenderingContext2D,
  image: HTMLCanvasElement,
  box: { x: number; y: number; width: number; height: number },
): { x: number; y: number; width: number; height: number } {
  const scale = Math.min(box.width / image.width, box.height / image.height);
  const width = Math.round(image.width * scale);
  const height = Math.round(image.height * scale);
  const x = Math.round(box.x + (box.width - width) / 2);
  const y = Math.round(box.y + (box.height - height) / 2);
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.6)';
  ctx.shadowBlur = 36;
  ctx.fillStyle = '#1a1310';
  roundedPath(ctx, x, y, width, height, 28);
  ctx.fill();
  ctx.restore();
  ctx.save();
  roundedPath(ctx, x, y, width, height, 28);
  ctx.clip();
  ctx.drawImage(image, x, y, width, height);
  ctx.restore();
  ctx.strokeStyle = 'rgba(242, 193, 78, 0.85)';
  ctx.lineWidth = 5;
  roundedPath(ctx, x, y, width, height, 28);
  ctx.stroke();
  return { x, y, width, height };
}

export async function renderShareCard(
  result: ShareableResult,
  visual: ShareVisual,
): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // 背景
  const scene = visual.sceneUrl ? await loadImage(visual.sceneUrl) : null;
  if (scene) {
    drawCover(ctx, scene);
  } else {
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, '#4a2c24');
    g.addColorStop(1, '#1d1110');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }
  const shade = ctx.createLinearGradient(0, 0, 0, H);
  shade.addColorStop(0, 'rgba(15,8,6,0.55)');
  shade.addColorStop(0.45, 'rgba(15,8,6,0.35)');
  shade.addColorStop(1, 'rgba(15,8,6,0.88)');
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, W, H);

  // 見出し
  text(ctx, result.stageTitle, W / 2, 150, 64, '#fff8ee', 800);
  if (visual.dailyLabel) {
    text(ctx, `${visual.dailyLabel}  みんな同じ順番で落ちてくる日`, W / 2, 230, 36, '#ffe27a', 800);
  } else {
    text(ctx, `${visual.regionName}の名物めしを合体させる落ちものパズル`, W / 2, 230, 34, '#f2dcc0', 500);
  }

  // 到達した料理
  const maxLevel = visual.items.length;
  const reached = visual.items.find((i) => i.level === visual.reachedLevel);
  const rest = maxLevel - visual.reachedLevel;
  const restText = rest > 0 ? `あと ${rest} 段で最上位の一品` : '最上位の一品に到達！';
  const restColor = rest > 0 ? '#f2c14e' : '#ffe27a';
  const combo = visual.maxCombo ?? 0;
  if (visual.moment) {
    // 左：名場面の盤面（2:3）と、その説明
    const board = drawBoard(ctx, visual.moment.image, { x: 60, y: 300, width: 470, height: 705 });
    text(ctx, visual.moment.caption, board.x + board.width / 2, 1050, 34, '#ffe27a', 800, 500);
    // 右：到達した料理・あと何段・最大コンボ
    const cx = 805;
    const colWidth = 470;
    if (reached) {
      const img = await loadImage(reached.iconUrl);
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.55)';
      ctx.shadowBlur = 40;
      if (img) drawContain(ctx, img, cx, 500, 400);
      ctx.restore();
      text(ctx, '最高到達', cx, 740, 34, '#f2dcc0', 700, colWidth);
      text(ctx, `Lv.${reached.level} ${reached.name}`, cx, 800, 54, '#fff8ee', 800, colWidth);
      text(ctx, restText, cx, 870, 36, restColor, 800, colWidth);
    }
    if (combo >= 2) {
      ctx.fillStyle = 'rgba(255,248,238,0.14)';
      roundedPath(ctx, cx - 190, 920, 380, 90, 45);
      ctx.fill();
      text(ctx, `最大コンボ ${combo}`, cx, 966, 46, '#fff8ee', 900, 360);
    }
  } else if (reached) {
    const img = await loadImage(reached.iconUrl);
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = 40;
    if (img) drawContain(ctx, img, W / 2, 620, 560);
    ctx.restore();
    text(ctx, `Lv.${reached.level} ${reached.name}`, W / 2, 960, 60, '#fff8ee', 800);
  }
  if (!visual.moment) text(ctx, restText, W / 2, 1045, 44, restColor, 800);

  // 進化の列（届いた段は絵、まだの段は「？」）
  const perRow = Math.ceil(maxLevel / 2);
  const cell = 150;
  const gap = 12;
  const rowWidth = perRow * cell + (perRow - 1) * gap;
  const left = (W - rowWidth) / 2;
  const icons = await Promise.all(
    visual.items.map((item) =>
      item.level <= visual.reachedLevel ? loadImage(item.iconUrl) : Promise.resolve(null),
    ),
  );
  visual.items.forEach((_item, i) => {
    const row = Math.floor(i / perRow);
    const col = i % perRow;
    const x = left + col * (cell + gap);
    const y = 1130 + row * (cell + gap);
    ctx.fillStyle = 'rgba(255,248,238,0.12)';
    roundedPath(ctx, x, y, cell, cell, 22);
    ctx.fill();
    const icon = icons[i];
    if (icon) {
      drawContain(ctx, icon, x + cell / 2, y + cell / 2, cell - 20);
    } else {
      text(ctx, '？', x + cell / 2, y + cell / 2, 64, 'rgba(255,248,238,0.55)', 800);
    }
  });

  // スコア
  const scoreY = 1130 + 2 * (cell + gap) + 90;
  text(ctx, `SCORE ${result.score.toLocaleString('ja-JP')}`, W / 2, scoreY, 58, '#fff8ee', 900);
  if (result.isNewRecord) text(ctx, 'ハイスコア更新！', W / 2, scoreY + 70, 40, '#ffe27a', 800);

  // 足もと
  text(ctx, visual.hashtags, W / 2, H - 170, 38, '#f2dcc0', 700);
  text(ctx, `${visual.host} でブラウザからすぐ遊べる`, W / 2, H - 100, 32, 'rgba(255,248,238,0.75)', 500);

  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png'));
}
